<template>
    <div>
        <canvas ref="canvas"></canvas>
    </div>
</template>
<script>
import Chart from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import * as ChartAnnotation from 'chartjs-plugin-annotation'
import PixelScale from './pixel-scale'
import NonReactiveChartData from './non-reactive-chart-data'

Chart.scaleService.registerScaleType('pixels', PixelScale, {})

import { register as registerSanePositioner } from './SaneTooltipPositioner'

registerSanePositioner(Chart.Tooltip.positioners)

// Hear ye all intruders!
// There be dragons here. Tread carefully.
//
// Chart.js and Vue.js don't want to play nicely together. If you feed an object full of data to
// Chart.js, it modifies that data object to store some state/metadata. This is useful from the
// perspective of Chart.js, since it means it doesn't have to shadow/track that state elsewhere.
// This is sort of a pain when using Vue.js, since you can't just watch the data object for
// changes and update Chart.js. When you update Chart.js, it modifies the data object, which
// triggers a (relatively) infinite loop in the reactivity system.
//
// Chart.js wants to write fields that follow the pattern:
// {root}.datasets[]._meta
//
// What we do here is take the (reactive) data object in, wrap it in a non-reactive layer by
// manually defining properties on some new objects (these properties point directly at the
// original reactive object), tack on a _meta field that _doesn't_ write back to the original
// reactive object, and pass this new hotness off to Chart.js. This lets Chart.js do its bookkeeping
// using the _meta field without triggering a constant loop of updates from Vue.js.
//
// This exists because the implementation in the npm package vue-chartjs ONLY is reactive if you
// completely replace the chart data object. Otherwise, you have to roll your own reactivity, at
// which point vue-chartjs isn't really adding much value.

const defaultOptionsByType = {
    line: {
        // default options you want for line charts
        legend: {
            display: false,
            padding: 24,
            fontColor: 'rgb(255, 99, 132)',
        },
        scales: {
            yAxes: [
                {
                    ticks: {
                        min: 0,
                        max: 100,
                        maxTicksLimit: 8,
                        fontColor: '#777',
                    },
                    gridLines: {
                        display: true,
                    },
                },
            ],
            xAxes: [
                {
                    gridLines: {
                        display: false,
                    },
                },
            ],
        },
    },
    bar: {
        // default options you want for bar charts
        legend: {
            display: false,
        },

        scales: {
            yAxes: [
                {
                    ticks: {
                        min: 0,
                        max: 100,
                        maxTicksLimit: 8,
                        callback: function (value, index, values) {
                            return '$' + value
                        },
                    },
                    gridLines: {
                        display: true,
                    },
                },
            ],
            xAxes: [
                {
                    ticks: {
                        min: 0,
                        max: 100,
                        maxTicksLimit: 12,
                        maxRotation: 0,
                        //minRotation: 90,
                    },
                    gridLines: {
                        display: false,
                    },
                },
            ],
        },
    },
    horizontalBar: {
        // default options you want for horizontal bar charts
        legend: {
            display: false,
        },

        scales: {
            yAxes: [
                {
                    ticks: {
                        min: 0,
                        max: 100,
                        maxTicksLimit: 8,
                    },
                    gridLines: {
                        display: false,
                    },
                },
            ],
            xAxes: [
                {
                    ticks: {
                        min: 0,
                        max: 50,
                        maxTicksLimit: 12,
                        maxRotation: 0,

                        //minRotation: 90,
                    },
                    gridLines: {
                        display: true,
                    },
                },
            ],
        },
    },
    pie: {
        // default options you want for pie charts
        tooltips: {
            callbacks: {
                label: function (tooltipItem, data) {
                    let label = data.labels[tooltipItem.index]
                    let value = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index].toFixed(2)
                    return label + ': ' + value
                },
            },
        },
        legend: {
            display: true,
        },
    },
    doughnut: {
        // default options you want for doughnut charts
        tooltips: {
            callbacks: {
                label: function (tooltipItem, data) {
                    let label = data.labels[tooltipItem.index]
                    let value = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index].toFixed(2)
                    return label + ': ' + value
                },
            },
        },
        legend: {
            display: true,
        },
    },
}

//Globl Chart Properties
var originalColors = ['#1CC8F0', '#93EEF9', '#DFF9FD']
var rainbow = ['#FF0000', '#FF8000', '#FFFF00', '#008000', '#0000FF', '#800080']
var singleAlpha = ['FF']
var multiAlpha = ['FF', '60']

var colors = originalColors
var alphas = multiAlpha

const GRID_LINE_WIDTH = 1.5
const GRID_LINE_COLOR = 'rgba(255,255,255,.65)'
const ZERO_LINE_COLOR = 'rgba(255,255,255,.65)'

Chart.helpers.merge(Chart.defaults, {
    scale: {
        display: true,
        gridLines: {
            drawOnChartArea: true,
            display: false,
            color: 'rgba(255,255,255,0.65)',
            lineWidth: GRID_LINE_WIDTH,
            drawBorder: false,
            zeroLineWidth: GRID_LINE_WIDTH,
            zeroLineColor: 'rgba(255,255,255,0.65)',
            tickMarkLength: 0,
        },
        scaleLabel: {
            display: false,
            fontSize: 14,
            fontColor: '#666',
            padding: {
                top: 0,
                bottom: 0,
            },
        },
        ticks: {
            display: false,
            fontColor: '#777',
            beginAtZero: false,
            sampleSize: 4,
            padding: 24,
            maxTicksLimit: 8,
            autoSkip: true,
            maxRotation: 16,
            maxTicksLimit: 8,
        },
    },
})

//Rounded Chart Corners

//Global Chart
Chart.helpers.merge(Chart.defaults.global, {
    responsive: true,
    maintainAspectRatio: false,
    defaultFontColor: '#666',
})
//Global Chart Layout
Chart.helpers.merge(Chart.defaults.global.layout, {
    padding: { left: 0, right: 0, top: 0, bottom: 0 },
})

// Global Options

Chart.helpers.merge(Chart.defaults.global.scales, {
    xAxes: [
        {
            display: false,
            type: 'time',
        },
    ],
    yAxes: [
        {
            display: false,
            type: 'linear',
            position: 'right',
        },
    ],
})

Chart.helpers.merge(Chart.defaults.global.datasets, {
    line: {
        datalabels: { display: false },
        borderWidth: 1.5,
    },
    bubble: {
        datalabels: { display: false },
        borderWidth: 1.5,
    },
})

//Global Chart Legends
Chart.Legend.prototype.afterFit = function () {
    this.height = this.height + 16
}

Chart.helpers.merge(Chart.defaults.global.legend, {
    position: 'top',
    align: 'end',
    labels: {
        boxWidth: 16,
        fontSize: 12,
        filter(item, data) {
            return !data.datasets[item.datasetIndex]?.hideLegend
        },
    },
})

//Global DataLabel Properties
Chart.helpers.merge(Chart.defaults.global.plugins.datalabels, {
    display: false,
    color: '#999',
    align: 'end',
    anchor: 'end',
    textAlign: 'center',
})

//Global Line Chart Properties
Chart.helpers.merge(Chart.defaults.global.elements.line, {
    tension: 0.1,
    borderWidth: 1,
    fontColor: 'red',
    borderColor: '#05DBF2',
    backgroundColor: 'rgba(0, 202, 234,.15)',
    fill: true,
})

//Global Point (Line Chart) Properties
Chart.helpers.merge(Chart.defaults.global.elements.point, {
    pointStyle: 'circle',
    radius: 2,

    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#05DBF2',
    hitRadius: 4,
    hoverRadius: 4,
    hoverBorderColor: '#05DBF2',
    hoverBackgroundColor: 'none',
})
//Global Bar Chart Properties
Chart.helpers.merge(Chart.defaults.global.elements.rectangle, {
    borderWidth: 0,
    backgroundColor: function (options) {
        return (
            colors[options.dataIndex % colors.length] +
            alphas[Math.floor(options.dataIndex / colors.length) % alphas.length]
        )
    },
    borderColor: '#05DBF2',
    borderSkipped: 'bottom',
})
//Global Pie/Doughnut Chart Properties
Chart.helpers.merge(Chart.defaults.global.elements.arc, {
    borderWidth: 1,
    backgroundColor: function (options) {
        return (
            colors[options.dataIndex % colors.length] +
            alphas[Math.floor(options.dataIndex / colors.length) % alphas.length]
        )
    },
    borderColor: '#05DBF2',
    borderSkipped: 'bottom',
})
//Global ToolTip Properties
Chart.helpers.merge(Chart.defaults.global.tooltips, {
    enabled: true,
    mode: 'index',
    position: 'average',
    intersect: false,
    yAlign: 'bottom',
    backgroundColor: 'rgb(255, 255, 255)',
    cornerRadius: 4,
    borderWidth: 1,
    borderColor: 'rgb(200, 200, 200)',
    caretSize: 0,
    titleFontColor: 'rgb(0, 0, 0)',
    bodyFontColor: 'rgb(0, 0, 0)',
    displayColors: false,
    xPadding: 8,
    yPadding: 8,
    titleAlign: 'center',
    bodyAlign: 'center',
    footerAlign: 'left',
})

export default {
    props: {
        chartType: {
            type: String,
            required: false,
            default: '',
        },
        chartData: {
            type: Object,
            default() {
                return {}
            },
        },
        options: {
            type: Object,
            default() {
                return {}
            },
        },
        width: {
            default: 400,
            type: Number,
        },
        height: {
            default: 400,
            type: Number,
        },
    },
    watch: {
        chartData: {
            handler() {
                this.$nextTick(() => {
                    if (this.chart) {
                        this.ensureNonReactiveChartData()
                        this.chart.update()
                    } else {
                        this.renderChart()
                    }
                })
            },
            deep: true,
        },
        chartType() {
            this.renderChart()
        },
        chartJsOptions() {
            this.$nextTick(() => {
                this.renderChart()
            })
        },
    },
    mounted() {
        this.chart = null
        this.renderChart()
    },
    methods: {
        generateLegend() {
            if (this.chart) {
                return this.chart.generateLegend()
            }
        },
        renderChart() {
            if (this.chart) {
                this.chart.destroy()
            }

            this.ensureNonReactiveChartData()

            this.chart = new Chart(this.$refs.canvas.getContext('2d'), {
                type: this.chartType,
                data: this.__nonReactive_chartData,
                options: this.chartJsOptions,
            })

            this.$emit('renderedChart')
        },
        ensureNonReactiveChartData() {
            // this is intentionally not reactive; this is a shadow of the real chart data so that
            // we can prevent chart.js from causing a feedback loop
            if (!this.__nonReactive_chartData) {
                this.__nonReactive_chartData = new NonReactiveChartData()
            }
            this.__nonReactive_chartData.update(this.chartData)
        },
    },
    beforeDestroy() {
        if (this.chart) {
            this.chart.destroy()
        }
    },
    computed: {
        chartJsOptions() {
            var options = {}
            Chart.helpers.merge(options, [defaultOptionsByType[this.chartType] || {}, this.options])
            return options
        },
    },
}
</script>
<style>
@import '~@/css/charts.css';
</style>
